import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useNotification } from "../_hooks/useNotification";

import { useAuth } from "./useAuth";

import { baseUrl } from "@/clientConfig";

const SESSION_EXPIRED_DELAY = 2000;

const apiOrigin = new URL(baseUrl).origin;

const isApiRequest = (input: Parameters<typeof fetch>[0]) =>
  new URL(
    input instanceof Request ? input.url : String(input),
    window.location.href,
  ).origin === apiOrigin;

/**
 * Hook para configurar el interceptor de errores de autenticación
 *
 * Seguridad implementada:
 * - Intercepta respuestas 401 y limpia la sesión
 * - Redirige automáticamente al login
 * - Previene ataques de sesión expirada
 */
export const useAuthInterceptor = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleAuthError = useCallback(
    async (message: string) => {
      notify({
        message,
        severity: "info",
        duration: 5000,
      });

      // Esperar para que usuario lea mensaje
      await new Promise((resolve) => {
        timeoutRef.current = setTimeout(resolve, SESSION_EXPIRED_DELAY);
      });

      logout();
      void navigate({ to: "/login" });
    },
    [logout, navigate, notify],
  );

  const notifyConnectionError = useCallback(() => {
    notify({
      message: t("session.connection_error"),
      severity: "info",
      duration: 5000,
    });
  }, [notify, t]);

  useEffect(() => {
    // Configurar interceptor de respuesta para manejar errores 401
    // IMPORTANTE: No guardamos originalFetch aquí porque necesitamos
    // que el wrapper funcione sobre el fetch que ya fue modificado por MSW
    // bind: `window.fetch` es un tipo unión (DOM + Node) y al desligarlo
    // perdería el receptor
    const currentFetch = window.fetch.bind(window);

    // Usar defineProperty en lugar de asignación directa: MSW instala el fetch
    // con writable:false (configurable:true), así que `window.fetch = X` falla.
    const installFetch = (impl: typeof window.fetch) => {
      Object.defineProperty(window, "fetch", {
        value: impl,
        writable: true,
        configurable: true,
      });
    };

    const interceptedFetch: typeof window.fetch = async (...args) => {
      const [input, init = {}] = args;
      const correlationId = crypto.randomUUID();

      const headers = new Headers(
        init.headers ?? (input instanceof Request ? input.headers : undefined),
      );
      headers.set("X-Correlation-ID", correlationId);

      try {
        // Llamar al fetch actual (que puede ser MSW u otro wrapper)
        const response = await currentFetch(input, { ...init, headers });

        if (!response.ok) {
          const responseCorrelationId =
            response.headers.get("X-Correlation-ID") ?? correlationId;

          Sentry.addBreadcrumb({
            category: "http",
            message: `${init.method ?? "GET"} ${input instanceof Request ? input.url : String(input)} -> ${response.status}`,
            data: { correlationId: responseCorrelationId },
            level: "error",
          });
          Sentry.setContext("correlation", {
            id: responseCorrelationId,
          });
        }

        // Si recibimos un 401, limpiar sesión y redirigir a login
        if (response.status === 401) {
          await handleAuthError(t("session.expired"));
        }

        return response;
      } catch (error) {
        // Solo manejar errores de red reales (TypeError con mensaje específico)
        // No manejar otros errores que podrían ser de lógica de negocio
        const isNetworkError =
          error instanceof TypeError &&
          (error.message.includes("Failed to fetch") ||
            error.message.includes("NetworkError") ||
            error.message.includes("Network request failed"));

        // Un error de red transitorio (p.ej. un 504 del ALB sin CORS, que llega
        // como "Failed to fetch") no implica sesión expirada: avisamos pero
        // conservamos la sesión y dejamos que el llamante reintente.
        if (isNetworkError && window.location.pathname !== "/login") {
          notifyConnectionError();
        }

        throw error;
      }
    };

    // Solo interceptar peticiones a nuestra API: añadir X-Correlation-ID a
    // terceros (Mixpanel, Sentry…) fuerza un preflight CORS que la rechaza,
    // y un 401 ajeno no debe cerrar la sesión.
    installFetch((input, init) =>
      isApiRequest(input)
        ? interceptedFetch(input, init)
        : currentFetch(input, init),
    );

    // Limpiar interceptor al desmontar
    return () => {
      installFetch(currentFetch);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [handleAuthError, notifyConnectionError, t]);
};
